import { RoutePage, RouteSettings } from './../app-routes.enum';
import { uniqBy } from 'lodash';
import { Observable, Subject as RxjsSubject, zip } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { BreakpointObserver } from '@angular/cdk/layout';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, SortDirection } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { NavigationExtras, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ApplicationError } from '../_errors/application-error';
import { SortFilter } from '../_objects/filter';
import { InspectionReportList, InspectionReportListItem } from '../_objects/inspection-report';
import { Subject } from '../_objects/subject';
import { SubjectModelListItem } from '../_objects/subject-model';
import { ConfigurationItem } from '../_objects/tenant-config';
import { TenantUser } from '../_objects/user';
import { UtilsService } from '../_services/_utils.service';
import { CommonSnackBarService } from '../_services/common-snack-bar.service';
import { InspectionReportService } from '../_services/http-api/inspection-report.service';
import { LoginUserOnlineService } from '../_services/http-api/login-user-online.service';
import { SubjectModelService } from '../_services/http-api/subject-model.service';
import { SubjectService } from '../_services/http-api/subject.service';
import { TenantConfigService } from '../_services/http-api/tenant-config.service';
import { TenantUserService } from '../_services/http-api/tenant-user.service';
import { LangUpdateService } from '../_services/lang-update.service';
import { NetworkStatusService } from '../_services/network-status.service';
import { SpinnerService } from '../_services/spinner.service';
import {
  INSPECTON_REPORT_LIST___REPORT_QUERY_PARAMS,
  ReportQueryParam,
  StorageService,
  SubjectFilter
} from '../_services/storage.service';
import { SearchSelectOption } from '../_shared/component/search-combo-box/search-select-option';
import {
  ComboBoxOption,
  FilterData,
  FilterSelectDialogComponent
} from '../_shared/dialog/filter-select-dialog/filter-select-dialog.component';
import {
  FILTER_CONTENT_ID,
  INSPECTION_REPORT_STATUS,
  INSPECTION_REPORT_STATUS_CODE,
  PAGINATION_VALUES,
  PERMISSION_CODE,
  TenantConfig
} from '../app.constants';
import { CommonDeleteDialogComponent } from '../common-dialog/common-delete-dialog.component';
import { SharedLockService } from '../_services/shared-lock/shared-lock.service';
import { MeUserService } from '../_services/http-api/me-user.service';
import { CallToActionLabel } from '../shared/directives/cta/cta.directive';
import { IconEnum } from '@evc/web-components';

export class InspectionReportItemWrapper extends InspectionReportListItem {
  constructor(listItem: InspectionReportListItem) {
    super();
    this.reportId = listItem.reportId;
    this.title = listItem.title;
    this.status = listItem.status;
    this.permission = listItem.permission;
    this.lastUpdateDate = listItem.lastUpdateDate;
    this.inspection = listItem.inspection;
    this.createdBy = listItem.createdBy;
    this.thumbFilePath = listItem.thumbFilePath;
  }

}

@Component({
    selector: 'app-inspection-report-list',
    templateUrl: './inspection-report-list.component.html',
    styleUrls: ['./inspection-report-list.component.scss'],
    animations: [
        trigger('reportExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0', visibility: 'hidden' })),
            state('expanded', style({ height: '*', visibility: 'visible' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    standalone: false
})

export class InspectionReportListComponent implements OnInit, OnDestroy, AfterViewInit {

  callToActionLabel: CallToActionLabel =
    {
      label: this.translate.instant('topMenuBar.reportTemplate'),
      icon: IconEnum.ArrowUpRightFromSquare
    };

  // Statusフィルタコンボ用
  reportStatus: Map<string, string> = new Map<string, string>(INSPECTION_REPORT_STATUS);
  reportStatusCodes: string[] = Array.from(INSPECTION_REPORT_STATUS.keys())
    .filter(code => code === INSPECTION_REPORT_STATUS_CODE.EDITING || code === INSPECTION_REPORT_STATUS_CODE.COMPLETED).sort();
  selectedStatus: string;

  // Subjectフィルタコンボ用
  subjectModels: SubjectModelListItem[];
  selectedSubjectModelId: string = null;
  private _subjectModelOpts: SearchSelectOption[];

  // S/Nフィルタコンボ用
  serialNumberLabel = TenantConfig.DefaultConfigurationValues.LabelOfSerialNumber;
  subjects: Subject[]; // 全候補
  selectedSubjectId: string = null; // 選択されたサブジェクト
  private _subjectOpt: SearchSelectOption[]; // 全候補から表示用に絞ったもの

  // Paginator用
  reportList: InspectionReportList;
  tenantUsers: TenantUser[] = [];
  tenantUserIds: string[] = [];
  reportDataSource = new MatTableDataSource<InspectionReportItemWrapper>();
  isOwn = false;
  isNarrow: boolean;
  isAdmin = false;
  sort: MatSort;
  selectFilters: FilterData[] = [];
  onDestroy$ = new RxjsSubject();

  DEFAULT_QUERY_PARAM: ReportQueryParam = {
    filter: { status: null, subjectFilter: null, isOwn: false, labels: [] },
    sort: { active: 'lastUpdateDate', direction: 'desc' },
    page: { index: 0, size: 10 },
    search: { text: null }
  };

  searchTextMaxLength = 1000;
  searchTextControl = new FormControl<string>(null, [Validators.maxLength(this.searchTextMaxLength)]);
  sortFilters: SortFilter[];
  selectedOwner: string;
  selectedCountry: string;
  selectedState: string;
  selectedDistrict: string;
  selectedSite: string;
  selectedProject: string;
  currentUser: string;
  isFocusedSearchInput: boolean = false;
  currentSearchWord: string = '';
  activeSort: string = '';
  hoveredSortType: string = null;
  isRecordsLoading = true;
  pageIndex = PAGINATION_VALUES.START_INDEX;
  pageSize = PAGINATION_VALUES.DEFAULT_PAGE_SIZE;
  onRefresh$ = new RxjsSubject();
  hoverIndex: number;
  isOnPremise = true;

  @ViewChild(MatSort) set Sort(value: MatSort) {
    this.reportDataSource.sort = value;
  }

  @ViewChild('scrollContainer', { static: false }) scrollContainer: ElementRef;
  @ViewChild('reportCardContents') cardListContents: ElementRef = new ElementRef(null);
  readonly reportCardWidth: number = 252;

  get isNoActiveSearchInput() {
    return !this.searchTextControl.value && !this.isFocusedSearchInput;
  }

  get isDesktop() {
    return this.networkStatusService.isDesktop();
  }

  get isOffline() {
    return this.networkStatusService.isOffline;
  }

  constructor(
    private router: Router,
    private subjectModelService: SubjectModelService,
    private subjectService: SubjectService,
    private inspectionReportService: InspectionReportService,
    private storageService: StorageService,
    private tenantUserService: TenantUserService,
    private dialog: MatDialog,
    private utilsService: UtilsService,
    private snackBar: CommonSnackBarService,
    private loginUserService: LoginUserOnlineService,
    private breakpointObserver: BreakpointObserver,
    private oscSpinnerService: SpinnerService,
    private networkStatusService: NetworkStatusService,
    private tenantConfigService: TenantConfigService,
    private translate: TranslateService,
    private langService: LangUpdateService,
    private sharedLockService: SharedLockService,
    private meUserService: MeUserService,
  ) {
    this.langService.prepare(translate, this.onDestroy$, (res) => {
      if (this.sortFilters) {
        this.sortFilters.filter(value => value.column === 'lastUpdateDate').forEach(value => value.label = this.translate.instant('inspectionreportlist.lastupdateDate'));
        this.sortFilters.filter(value => value.column === 'title').forEach(value => value.label = this.translate.instant('inspectionlist.reportName'));
      }
    });
    this.breakpointObserver.observe(['(max-width: 920px)'])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(result => {
        this.isNarrow = result.matches;
      });
  }

  ngOnInit() {
    this.oscSpinnerService.show();
    this.initSortFilters();
    this.changeSortFilter(true);
    this.isOnPremise = this.networkStatusService.isOnPremise;
  }

  ngAfterViewInit() {
    this.isAdmin = this.loginUserService.isAdminRole();

    zip(
      // 画面の初期化に必要な情報をサービスから取得する
      this.subjectModelService.getSubjectModels(),
      this.subjectService.getSubjects(),
      this.tenantConfigService.getTenantConfigs()
    )
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        val => this.setMasters(...val),
        error => {
          console.log(error);
          this.snackBar.open(this.translate.instant('inspectionreportlist.ngOnInitFailed'));
          this.oscSpinnerService.hide();
        },
        () => {
          this.initDisplay();
        }
      );
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
    this.onRefresh$.next(undefined);
    this.onRefresh$.unsubscribe();
    // Quick fix for the release of october 24
  }

  set subjectModelOpts(value: SearchSelectOption[]) {
    // 未選択用オプションを先頭に追加する。
    const defaultOpt = [new SearchSelectOption(null, this.translate.instant('inspectionreportlist.all'))];
    this._subjectModelOpts = defaultOpt.concat(value);
  }

  set subjectOpts(value: SearchSelectOption[]) {
    // 未選択用オプションを先頭に追加する。
    const defaultOpt = [new SearchSelectOption(null, this.translate.instant('inspectionreportlist.all'))];
    this._subjectOpt = defaultOpt.concat(value);
  }

  get subjectModelOpts(): SearchSelectOption[] {
    return this._subjectModelOpts;
  }

  get subjectOpts(): SearchSelectOption[] {
    return this._subjectOpt;
  }

  get isTitleAsc() {
    return this.sort?.active === 'title'
      ? this.sort?.direction === 'asc'
      : true;
  }

  get isDateAsc() {
    return this.sort?.active === 'lastUpdateDate'
      ? this.sort?.direction === 'asc'
      : false;
  }

  get isReportFound(): boolean {
    return (this.reportList?.items?.length ?? 0) > 0;
  }
  get isDateSortValid() {
    return this.sort?.active === 'lastUpdateDate';
  }

  /** マスターデータを格納する
   * @param  {SubjectModelListItem[]} subjectModels
   * @param  {Subject[]} subjects
   * @param tenantConfigs
   * @returns void
   */
  setMasters(subjectModels: SubjectModelListItem[], subjects: Subject[], tenantConfigs: ConfigurationItem[]): void {
    // Modelの初期設定
    this.subjectModels = subjectModels.sort((subjectModel1, subjectModel2) => {
      if (subjectModel1.name < subjectModel2.name) { return -1; }
      if (subjectModel1.name > subjectModel2.name) { return 1; }
      return 0;
    });

    this.subjectModelOpts = this.subjectModels.map(_ => new SearchSelectOption(_.id, _.name));

    // S/Mの初期設定
    this.subjects = subjects.sort((subject1, subject2) => {
      if (subject1.serialNo < subject2.serialNo) { return -1; }
      if (subject1.serialNo > subject2.serialNo) { return 1; }
      return 0;
    });

    this.subjectOpts = this.subjects.map(_ => new SearchSelectOption(_.id, _.serialNo));

    this.tenantConfigService.setTenantConfig(tenantConfigs);
    if (tenantConfigs) {
      const snLabelConfig = tenantConfigs.find(_ => _.key === TenantConfig.LabelOfSerialNumber.KEY);
      if (snLabelConfig) {
        this.serialNumberLabel = snLabelConfig.value as string;
      }
    }
  }

  /** 画面初期化処理
   * @returns void
   */
  initDisplay(): void {
    const param = this.createInitQueryParam();
    this.initSort(param.sort.active, param.sort.direction);
    this.initSearchBox(param.search.text);
    this.initFilter(param.filter.status, param.filter.subjectFilter, param.filter.isOwn,
      this.reportStatus, this.subjectModels, this.subjects);
    this.selectFilters = param.filter.labels;

    // 最後にテーブルの初期化
    this.updateReportList(true);
  }

  createInitQueryParam(): ReportQueryParam {
    const param = this.storageService.fetchReportQueryParam(INSPECTON_REPORT_LIST___REPORT_QUERY_PARAMS);
    if (param) {
      return param;
    }
    return this.DEFAULT_QUERY_PARAM; // ストレージになかったら初期値
  }

  initFilter(status: string, subjectFilter: SubjectFilter, isOwn: boolean,
    statuses: Map<string, string>, subjectModels: SubjectModelListItem[], subjects: Subject[]) {

    let dstStatus = status;
    let dstSubjectModelId = subjectFilter?.subjectModelId;
    let dstSubjectId = subjectFilter?.subjectId;
    let dstIsOwn = isOwn;
    let dstOwner = subjectFilter?.owner;
    let dstCountry = subjectFilter?.country;
    let dstState = subjectFilter?.state;
    let dstDistrict = subjectFilter?.district;
    let dstSite = subjectFilter?.site;
    let dstProject = subjectFilter?.project;

    if (!this.validateFilter(status, subjectFilter, isOwn, statuses, subjectModels, subjects)) {
      // 不正だったらデフォルト値を使用する
      dstStatus = this.DEFAULT_QUERY_PARAM.filter.status;
      dstSubjectModelId = this.DEFAULT_QUERY_PARAM.filter.subjectFilter?.subjectModelId;
      dstSubjectId = this.DEFAULT_QUERY_PARAM.filter.subjectFilter?.subjectId;
      dstIsOwn = this.DEFAULT_QUERY_PARAM.filter.isOwn;
      dstOwner = this.DEFAULT_QUERY_PARAM.filter.subjectFilter?.owner;
      dstCountry = this.DEFAULT_QUERY_PARAM.filter.subjectFilter?.country;
      dstState = this.DEFAULT_QUERY_PARAM.filter.subjectFilter?.state;
      dstDistrict = this.DEFAULT_QUERY_PARAM.filter.subjectFilter?.district;
      dstSite = this.DEFAULT_QUERY_PARAM.filter.subjectFilter?.site;
      dstProject = this.DEFAULT_QUERY_PARAM.filter.subjectFilter?.project;
    }

    this.selectedStatus = dstStatus;
    this.selectedSubjectModelId = dstSubjectModelId;
    this.selectedSubjectId = dstSubjectId;
    this.isOwn = dstIsOwn;
    this.selectedOwner = dstOwner;
    this.selectedCountry = dstCountry;
    this.selectedState = dstState;
    this.selectedDistrict = dstDistrict;
    this.selectedSite = dstSite;
    this.selectedProject = dstProject;
  }

  initSort(active: string, direction: string) {
    let dstActive = active;
    let dstDirection = direction;

    if (!this.validateSort(direction)) {
      // 不正だったらデフォルト値を使用する
      dstActive = this.DEFAULT_QUERY_PARAM.sort.active;
      dstDirection = this.DEFAULT_QUERY_PARAM.sort.direction;
    }

    const tmpSort = new MatSort();
    tmpSort.start = dstDirection as 'asc' | 'desc';
    tmpSort.active = dstActive;
    tmpSort.direction = dstDirection as SortDirection;
    this.reportDataSource.sort = tmpSort;
    this.sort = tmpSort;

    this.initSortFilters();
  }

  initSearchBox(word: string) {
    let searchWord = word;

    if (!this.validateSearchBox(searchWord)) {
      // 不正だったらデフォルト値を使用する
      searchWord = this.DEFAULT_QUERY_PARAM.search.text;
    }
    this.searchTextControl.setValue(searchWord);
    this.currentSearchWord = searchWord;
  }

  deleteSearchWord() {
    this.searchTextControl.setValue('');
    if (this.currentSearchWord !== '') {
      this.initReportList();
    }
  }

  initReportList() {
    this.oscSpinnerService.show(true);
    this.updateReportList(true);
  }

  updateReportList(isRefresh = false) {
    this.isRecordsLoading = true;

    if (isRefresh) {
      this.onRefresh$.next(undefined);
      this.pageIndex = PAGINATION_VALUES.START_INDEX;
      this.pageSize = PAGINATION_VALUES.DEFAULT_PAGE_SIZE;
    }

    const subjectFilter = new SubjectFilter(
      this.selectedSubjectModelId,
      this.selectedSubjectId,
      this.selectedOwner,
      this.selectedCountry,
      this.selectedState,
      this.selectedDistrict,
      this.selectedSite,
      this.selectedProject
    );

    const queryParam = this.genQueryParams(
      this.selectedStatus,
      subjectFilter,
      this.isOwn,
      this.sort?.active,
      this.sort?.direction,
      this.pageIndex,
      this.pageSize,
      this.searchTextControl.value,
      this.selectFilters
    );

    this.currentSearchWord = queryParam.search.text;

    this.updateReportListSub(queryParam)
      .pipe(
        takeUntil(this.onDestroy$),
        takeUntil(this.onRefresh$),
      )
      .subscribe({
        next: response => {
          if (isRefresh) {
            this.reportList = new InspectionReportList();
          }

          this.reportList.total = response.total;
          this.reportList.page = response.page;

          this.reportList.items = isRefresh
            ? response.items
            : uniqBy([...this.reportList.items, ...response.items], 'reportId');

          this.resetScrollBarPosition();
        },
        error: error => {
          console.log(error);
          this.isRecordsLoading = false;
          this.snackBar.open(this.translate.instant('inspectionreportlist.updateReportListFailed'));
        },
        complete: () => {
          this.isRecordsLoading = false;
          this.handleReportList(this.reportList);
          this.oscSpinnerService.hide();
        }
      });
  }

  updateReportListSub(param: ReportQueryParam): Observable<InspectionReportList> {
    // 検索条件を内部ストレージに保存
    this.storageService.pushReportQueryParam(INSPECTON_REPORT_LIST___REPORT_QUERY_PARAMS, param);

    return this.inspectionReportService.getInspectionReports(null,
      param.filter.status, param.filter.subjectFilter, param.filter.isOwn,
      { active: param.sort.active, direction: param.sort.direction },
      { index: param.page.index + 1, size: param.page.size },
      param.search.text);
  }

  /** 検索条件の検証
   * @param  {ReportQueryParam} param
   * @returns boolean
   */
  validateQueryParam(param: ReportQueryParam): boolean {
    if (!param || !param.sort) {
      return false;
    }

    // Angularアップデート対応
    // ActiveとDirectionがnullまたはundefinedの場合、ローカルストレージの値を参照する
    // dataSourceのデータが空の場合、sortがUndefinedとなり必ず不正となってしまうため
    const initParam = this.createInitQueryParam();
    param.sort.active = param.sort.active ?? initParam.sort.active;
    param.sort.direction = param.sort.direction ?? initParam.sort.direction;

    return this.validate(param, this.reportStatus, this.subjectModels,
      this.subjects);
  }

  // TotalとpageIndexを更新する
  handleReportList(reportList: InspectionReportList) {
    if (!reportList || (!reportList.total && reportList.total !== 0) || reportList.total < 0 ||
      !reportList.page || reportList.page < 1 || !reportList.items) {
      return;
    }
    this.loadInspectionReportItemWrapper(reportList.items);
    this.getTenantUsers();
  }

  getTenantUsers() {
    this.tenantUserService.getUsers().subscribe((users)=>{
      this.tenantUsers = users;
      this.tenantUserIds = this.tenantUsers.filter(user => user.id).map(user => user.id);
    });
  }

  loadInspectionReportItemWrapper(items: InspectionReportListItem[]): void {
    const wrapperList = items.map(item => {
      return new InspectionReportItemWrapper(item);
    });

    this.reportDataSource = new MatTableDataSource<InspectionReportItemWrapper>(wrapperList);
  }

  copyValue(src: any, dst: MatPaginator) {
    dst.length = src.length;
    dst.pageSize = src.pageSize;
    dst.pageIndex = src.pageIndex;
  }

  deleteReport(reportId: string): void {
    const report = this.reportList.items.find(item => item.reportId === reportId);

    if (!report || !report.reportId) {
      throw new ApplicationError('Report is invalid.');
    }

    const titles = [];
    titles.push(report.title);

    this.openDeleteDialog(titles).subscribe(result => {
      if (result) {
        this.deleteReportSub(report.reportId);
      }
    });
  }

  openDeleteDialog(titles: string[]): Observable<any> {
    const dialogRef = this.dialog.open(CommonDeleteDialogComponent, {
      width: '600px',
      minWidth: '400px',
      maxHeight: '800px',
      data: { target: (this.translate.instant('inspectionreportlist.inspectionReport')), deleteNames: titles }
    });
    return dialogRef.afterClosed();
  }

  deleteReportSub(id: string): void {
    this.oscSpinnerService.show();

    this.inspectionReportService.deleteInspectionReport(id).subscribe(
      val => { },
      error => {
        this.oscSpinnerService.hide();
        this.snackBar.open(this.translate.instant('inspectionreportlist.deleteReportSubFailed'));
        console.log(error);
      },
      () => this.initReportList()
    );
  }

  // レポート選択イベント用（レポート画面）
  gotoInspectionReport(report: InspectionReportItemWrapper): void {
    // 他ユーザーによる共有ロックは遷移しない
    if (this.isReportLock(report) && !this.isSharedLockByUser(report)) {
      return;
    }

    let param: NavigationExtras;
    if (this.isDesktop && this.networkStatusService.isOnline && this.isSharedLockByUser(report)) {
      param = { queryParams: { offlineDataUpdate: true } };
    }

    this.router.navigate([RoutePage.InspectionReport, report.reportId], param);
  }

  filterSubjects(): void {
    if (!this.subjects) {
      throw new ApplicationError('Inspection report is invalid.');
    }

    // ModelがALL、または同じなら選択肢になる。
    this.subjectOpts = this.subjects
      .filter(subject => !this.selectedSubjectModelId || subject.subjectModelId === this.selectedSubjectModelId)
      .map(subject => new SearchSelectOption(subject.id, subject.serialNo));

    // CurrentのS/Nが、CurrentのSubjectModelのものでなかったら、null(ALL)に変更する
    if (Array.from(this.subjectOpts, subject => subject.value).indexOf(this.selectedSubjectId) < 0) {
      this.selectedSubjectId = null;
    }
  }

  validate(param: ReportQueryParam, statuses: Map<string, string>,
    subjectModels: SubjectModelListItem[], subjects: Subject[]): boolean {

    return this.validatePage(param.page.index, param.page.size)
      && this.validateSort(param.sort.direction)
      && this.validateFilter(param.filter.status, param.filter.subjectFilter, param.filter.isOwn, statuses, subjectModels, subjects)
      && this.validateSearchBox(param.search.text);
  }

  validatePage(pageIndex: number, pageSize: number): boolean {
    if (this.utilsService.isNullOrUndefined(pageIndex) || pageIndex < 0) {
      return false;
    }
    return !(this.utilsService.isNullOrUndefined(pageSize));
  }

  validateSort(sortDirection: string) {
    return !('asc' !== sortDirection && 'desc' !== sortDirection);
  }

  validateFilter(status: string, subjectFilter: SubjectFilter, isOwn: boolean,
    statuses: Map<string, string>, subjectModels: SubjectModelListItem[], subjects: Subject[]): boolean {

    if (!statuses ||
      !this.utilsService.isNullOrUndefined(status) && Array.from(statuses.keys()).indexOf(status) < 0) {
      return false;
    }
    if (!subjectModels
      || !this.utilsService.isNullOrUndefined(subjectFilter?.subjectModelId)
      && subjectModels.map(model => model.id).indexOf(subjectFilter?.subjectModelId) < 0) {
      return false;
    }
    if (!subjects
      || !this.utilsService.isNullOrUndefined(subjectFilter?.subjectId)
      && subjects.map(subject => subject.id).indexOf(subjectFilter?.subjectId) < 0) {
      return false;
    }
    if (isOwn === null || isOwn === undefined) {
      return false;
    }

    return (!subjectFilter?.owner || this.subjects.some(s => s.owner === subjectFilter?.owner))
      && (!subjectFilter?.country || this.subjects.some(s => s.country === subjectFilter?.country))
      && (!subjectFilter?.state || this.subjects.some(s => s.state === subjectFilter?.state))
      && (!subjectFilter?.district || this.subjects.some(s => s.district === subjectFilter?.district))
      && (!subjectFilter?.site || this.subjects.some(s => s.site === subjectFilter?.site))
      && (!subjectFilter?.project || this.subjects.some(s => s.project === subjectFilter?.project));
  }

  validateSearchBox(searchWord: string): boolean {
    return !(!this.utilsService.isNullOrUndefined(searchWord) && searchWord.length > this.searchTextMaxLength);
  }
  isReadOnly(report: InspectionReportItemWrapper): boolean {
    return !report.permission || (report.permission & PERMISSION_CODE.WRITE) === 0;
  }

  /** 検索条件オブジェクトを作成する
   * @param  {string} status
   * @param subjectFilter
   * @param  {boolean} isOwn
   * @param  {string} sortActive
   * @param  {string} sortDirection
   * @param  {number} pageIndex
   * @param  {number} pageSize
   * @param  {string} searchText
   * @param labels
   * @returns ReportQueryParam
   */
  genQueryParams(status: string, subjectFilter: SubjectFilter, isOwn: boolean,
    sortActive: string, sortDirection: string,
    pageIndex: number, pageSize: number, searchText: string, labels: FilterData[]): ReportQueryParam {
    return {
      filter: { status: status, subjectFilter: subjectFilter, isOwn: isOwn, labels: labels },
      sort: { active: sortActive, direction: sortDirection },
      page: { index: pageIndex, size: pageSize },
      search: { text: searchText }
    };
  }

  openAddFilterDialog(): void {
    const dialogRef = this.dialog.open(FilterSelectDialogComponent, {
      width: '560px',
      panelClass: 'dialog-scroll',
      data: {
        comboBoxOptions: this.genComboBoxOpts()
      }
    });

    dialogRef.componentInstance.changedValue
      .pipe(takeUntil(this.onDestroy$), takeUntil(dialogRef.afterClosed()))
      .subscribe(contentId => {

        // subjectModelを選択した際、subjectの一覧をsubjectModel関連のみにフィルタリングする
        if (contentId === FILTER_CONTENT_ID.SUBJECT_MODEL) {
          const baseIndex = dialogRef.componentInstance.comboBoxOptions.findIndex(opt => opt.contentId === FILTER_CONTENT_ID.SUBJECT_MODEL);
          const baseOpt = dialogRef.componentInstance.comboBoxOptions[baseIndex];
          const targetIndex = dialogRef.componentInstance.comboBoxOptions.findIndex(opt => opt.contentId === FILTER_CONTENT_ID.SUBJECT);
          const targetOpt = dialogRef.componentInstance.comboBoxOptions[targetIndex];

          // ModelがALL、または同じならコンボボックスに表示する。
          targetOpt.options = this.subjects
            .filter(subject => (!baseOpt.selectedValue) || (subject.subjectModelId === baseOpt.selectedValue))
            .map(subject => new SearchSelectOption(subject.id, subject.serialNo));

          // CurrentのS/Nが、CurrentのSubjectModelのものでなかったら、null(ALL)に変更する
          if (Array.from(targetOpt.options, subject => subject.value).indexOf(targetOpt.selectedValue as string) < 0) {
            targetOpt.selectedValue = null;
          }
        }
      });

    dialogRef.componentInstance.changedFilter
      .pipe(takeUntil(this.onDestroy$), takeUntil(dialogRef.afterClosed()))
      .subscribe(result => {
        this.selectFilters = result;
        this.setFilter(result);
        this.initReportList();
        dialogRef.close();
      });
  }

  setFilter(filters: FilterData[]) {
    this.selectedStatus = filters.some(filter => filter.id === FILTER_CONTENT_ID.STATUS)
      ? filters.find(filter => filter.id === FILTER_CONTENT_ID.STATUS).value as string
      : null;

    this.selectedSubjectModelId = filters.some(filter => filter.id === FILTER_CONTENT_ID.SUBJECT_MODEL)
      ? filters.find(filter => filter.id === FILTER_CONTENT_ID.SUBJECT_MODEL).value as string
      : null;

    this.selectedSubjectId = filters.some(filter => filter.id === FILTER_CONTENT_ID.SUBJECT)
      ? filters.find(filter => filter.id === FILTER_CONTENT_ID.SUBJECT).value as string
      : null;

    this.isOwn = filters.some(filter => filter.id === FILTER_CONTENT_ID.IS_OWN)
      ? filters.find(filter => filter.id === FILTER_CONTENT_ID.IS_OWN).value as boolean
      : false;

    this.selectedOwner = filters.some(filter => filter.id === FILTER_CONTENT_ID.OWNER)
      ? filters.find(filter => filter.id === FILTER_CONTENT_ID.OWNER).value as string
      : null;

    this.selectedCountry = filters.some(filter => filter.id === FILTER_CONTENT_ID.COUNTRY)
      ? filters.find(filter => filter.id === FILTER_CONTENT_ID.COUNTRY).value as string
      : null;

    this.selectedState = filters.some(filter => filter.id === FILTER_CONTENT_ID.STATE)
      ? filters.find(filter => filter.id === FILTER_CONTENT_ID.STATE).value as string
      : null;

    this.selectedDistrict = filters.some(filter => filter.id === FILTER_CONTENT_ID.DISTRICT)
      ? filters.find(filter => filter.id === FILTER_CONTENT_ID.DISTRICT).value as string
      : null;

    this.selectedSite = filters.some(filter => filter.id === FILTER_CONTENT_ID.SITE)
      ? filters.find(filter => filter.id === FILTER_CONTENT_ID.SITE).value as string
      : null;

    this.selectedProject = filters.some(filter => filter.id === FILTER_CONTENT_ID.PROJECT)
      ? filters.find(filter => filter.id === FILTER_CONTENT_ID.PROJECT).value as string
      : null;
  }

  filterClear(filter: FilterData): void {
    this.selectFilters = this.selectFilters.filter(obj => obj.id !== filter.id);
    this.setFilter(this.selectFilters);
    this.initReportList();
  }

  initSortFilters(): void {
    this.sortFilters = [
      new SortFilter('lastUpdateDate', this.translate.instant('inspectionreportlist.lastupdateDate'), false),
      new SortFilter('lastUpdateDate', this.translate.instant('inspectionreportlist.lastupdateDate'), true),
      new SortFilter('title', this.translate.instant('inspectionlist.reportName'), false),
      new SortFilter('title', this.translate.instant('inspectionlist.reportName'), true),
    ];
  }

  changeSortFilter(isDate: boolean) {
    let sortIndex: number;
    if (isDate) {
      sortIndex = this.isDateSortValid ? (this.isDateAsc ? 0 : 1) : 0;
      this.activeSort = "date";
    } else {
      sortIndex = this.isDateSortValid ? 3 : (this.isTitleAsc ? 2 : 3);
      this.activeSort = "name";
    }
    const sort = this.sortFilters[sortIndex];

    if (!this.reportDataSource.sort && this.sort) {
      this.reportDataSource.sort = this.sort;
    }

    const tmpSort = new MatSort();
    tmpSort.active = sort.column;
    tmpSort.direction = sort.direction as SortDirection;
    this.sort = tmpSort;
    this.reportDataSource.sort = tmpSort;
    this.initReportList();
  }

  genComboBoxOpts(): ComboBoxOption[] {
    const comboBoxOptions: ComboBoxOption[] = [];
    const defaultOpt = [new SearchSelectOption(null, this.translate.instant('inspectionreportlist.all'))];
    const statusValues = this.reportStatusCodes.map(st =>
      new SearchSelectOption(st, this.translate.instant('inspectionreportlist.status_' + st.toString().toLowerCase().replace(' ', ''))));
    const status = new ComboBoxOption(FILTER_CONTENT_ID.STATUS,
      this.translate.instant('inspectionreportlist.status'),
      defaultOpt.concat(statusValues),
      this.selectedStatus);
    comboBoxOptions.push(status);

    const subjectModelOpts = new ComboBoxOption(FILTER_CONTENT_ID.SUBJECT_MODEL,
      this.translate.instant('inspectionreportlist.model'),
      this.subjectModelOpts,
      this.selectedSubjectModelId);
    comboBoxOptions.push(subjectModelOpts);

    const subjectOpts = new ComboBoxOption(FILTER_CONTENT_ID.SUBJECT,
      this.serialNumberLabel,
      this.subjectOpts,
      this.selectedSubjectId);
    comboBoxOptions.push(subjectOpts);

    const isOwn = new ComboBoxOption(FILTER_CONTENT_ID.IS_OWN,
      this.translate.instant('inspectionreportlist.myReports'),
      null,
      this.isOwn,
      true);
    comboBoxOptions.push(isOwn);

    const ownerOpts: SearchSelectOption[] = [];
    this.subjects
      .filter(subject => subject.owner !== '' && !this.utilsService.isNullOrUndefined(subject.owner))
      .forEach(subject => {
        if (!ownerOpts.some(s => s.value === subject.owner)) { // 重複しないようにオプションリストを作成
          ownerOpts.push(new SearchSelectOption(subject.owner, subject.owner));
        }
      });
    const owner = new ComboBoxOption(FILTER_CONTENT_ID.OWNER,
      this.translate.instant('inspectionreportlist.owner'),
      defaultOpt.concat(ownerOpts),
      this.selectedOwner);
    comboBoxOptions.push(owner);

    const countryOpts: SearchSelectOption[] = [];
    this.subjects
      .filter(subject => subject.country !== '' && !this.utilsService.isNullOrUndefined(subject.country))
      .forEach(subject => {
        if (!countryOpts.some(s => s.value === subject.country)) { // 重複しないようにオプションリストを作成
          countryOpts.push(new SearchSelectOption(subject.country, subject.country));
        }
      });
    const country = new ComboBoxOption(FILTER_CONTENT_ID.COUNTRY,
      this.translate.instant('inspectionreportlist.country'),
      defaultOpt.concat(countryOpts),
      this.selectedCountry);
    comboBoxOptions.push(country);

    const stateOpts: SearchSelectOption[] = [];
    this.subjects
      .filter(subject => subject.state !== '' && !this.utilsService.isNullOrUndefined(subject.state))
      .forEach(subject => {
        if (!stateOpts.some(s => s.value === subject.state)) { // 重複しないようにオプションリストを作成
          stateOpts.push(new SearchSelectOption(subject.state, subject.state));
        }
      });
    const stateOption = new ComboBoxOption(FILTER_CONTENT_ID.STATE,
      this.translate.instant('inspectionreportlist.state'),
      defaultOpt.concat(stateOpts),
      this.selectedState);
    comboBoxOptions.push(stateOption);

    const districtOpts: SearchSelectOption[] = [];
    this.subjects
      .filter(subject => subject.district !== '' && !this.utilsService.isNullOrUndefined(subject.district))
      .forEach(subject => {
        if (!districtOpts.some(s => s.value === subject.district)) { // 重複しないようにオプションリストを作成
          districtOpts.push(new SearchSelectOption(subject.district, subject.district));
        }
      });
    const district = new ComboBoxOption(FILTER_CONTENT_ID.DISTRICT,
      this.translate.instant('inspectionreportlist.district'),
      defaultOpt.concat(districtOpts),
      this.selectedDistrict);
    comboBoxOptions.push(district);

    const siteOpts: SearchSelectOption[] = [];
    this.subjects
      .filter(subject => subject.site !== '' && !this.utilsService.isNullOrUndefined(subject.site))
      .forEach(subject => {
        if (!siteOpts.some(s => s.value === subject.site)) { // 重複しないようにオプションリストを作成
          siteOpts.push(new SearchSelectOption(subject.site, subject.site));
        }
      });
    const site = new ComboBoxOption(FILTER_CONTENT_ID.SITE,
      'Site',
      defaultOpt.concat(siteOpts),
      this.selectedSite);
    comboBoxOptions.push(site);

    const projectOpts: SearchSelectOption[] = [];
    this.subjects
      .filter(subject => subject.project !== '' && !this.utilsService.isNullOrUndefined(subject.project))
      .forEach(subject => {
        if (!projectOpts.some(s => s.value === subject.project)) { // 重複しないようにオプションリストを作成
          projectOpts.push(new SearchSelectOption(subject.project, subject.project));
        }
      });
    const project = new ComboBoxOption(FILTER_CONTENT_ID.PROJECT,
      'Project',
      defaultOpt.concat(projectOpts),
      this.selectedProject);
    comboBoxOptions.push(project);

    return comboBoxOptions;
  }

  isReportLock(report: InspectionReportItemWrapper) {
    return this.sharedLockService.isReportLocked(report.inspection);
  }

  isSharedLockByUser(report: InspectionReportItemWrapper) {
    return this.sharedLockService.isLockedByCurrentUser(report.inspection);
  }

  onLoadNext() {
    if (!this.isRecordsLoading && this.reportList?.items.length < this.reportList.total) {
      this.isRecordsLoading = true;
      this.pageIndex++;
      this.updateReportList();
    }
  }

  getDynamicScrollDistance(): number {
    const itemsLength = this.reportList?.items.length || 0;
    if (itemsLength < 100) {
      return 6;
    } else if (itemsLength < 500) {
      return 3;
    } else {
      return 2;
    }
  }

  /**
   * Resetting the scroll bar position avoids continuous load when the scroll bar is at the bottom.
   * @private
   */
  private resetScrollBarPosition() {
    const scrollContainer = this.scrollContainer?.nativeElement;
    const offsetHeight = scrollContainer?.offsetHeight;
    const buffer = offsetHeight * 0.1; // 10% of the visible height
    if (scrollContainer) {
      scrollContainer.scrollTop = scrollContainer.scrollTop - buffer;
    }
  }

  get isExistsSpace() {
    return this.getLastRowCount() !== 0;
  }

  getRowCount() {
    const contentWidth = this.cardListContents?.nativeElement?.offsetWidth;
    if (!contentWidth) {
      return 0;
    }

    return Math.floor(contentWidth / this.reportCardWidth);
  }

  getLastRowCount() {

    return this.reportDataSource?.data.length % this.getRowCount();
  }

  gotoReportTemplateBuilder(): void {
    if (this.networkStatusService.isOnPremise) {
      this.router.navigate([RoutePage.Settings, RouteSettings.OfflineReportTemplate]);
    } else {
      window.open(this.meUserService.getReportTemplateBuilderEndpoint(['template-list'], '?application=ViSOL'), '_blank');
    }
  }
}
